// Server-authoritative chat session context.
//
// Pre-v2: localStorage held the full ChatSession[] (heavy per-message
// events + per-session accumulated graph) and hit the ~5 MB origin
// quota. Post-v2: the server is the source of truth. This context wires
// useChats/useChat from lib/queries.ts and synthesizes the legacy
// ChatSession[] / activeSession shape so existing consumers
// (Sidebar, ChatApp, ActiveStreamsContext, GraphPanel) don't need to
// change.
//
// Local state held in-memory only:
//   - `drafts`: sessions the user created in the SPA but hasn't sent a
//     turn for yet. The server has no row until the first turn arrives.
//   - `temps`: temporary sessions explicitly marked `temporary` — never
//     hit the server, never persisted across reload.
//   - `activeSessionId`: mirrored to sessionStorage so a tab reload
//     restores the open chat but a new tab starts fresh.
//
// Accumulated graph sync: every updateSessionMessages call with a non-
// equal graph schedules a per-chat debounced PATCH. On session switch
// and on unmount/beforeunload, pending writes flush so we never lose
// the most-recent edit to an inactive chat.

import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  ChatSession,
  ChatFolder,
  StoredMessage,
  AccumulatedGraph,
} from '../types';
import type { ChatSummary } from '../lib/feedbackApi';
import { detailToSession, summaryToSessionStub } from '../lib/chatAdapters';
import { updateAccumulatedGraph } from '../lib/feedbackApi';
import type { BulkExportScope } from '../lib/export';
import ExportDialog from '../features/sidebar/ExportDialog';
import {
  chatKey,
  chatsKey,
  useChat,
  useChats,
  useCreateFolder,
  useDeleteChat,
  useDeleteFolder,
  useFolderCollapse,
  useFolders,
  usePatchChat,
  usePatchFolder,
  useSetChatFolder,
  useSetChatPinned,
  useSetFolderCollapse,
  PIN_KEY_PREFIX,
} from '../lib/queries';
import { runV2Migration } from '../lib/chatBackfill';

const ACTIVE_ID_KEY = 'malpedia-active-session-id';
const GRAPH_PATCH_DEBOUNCE_MS = 1000;

export interface SessionContextValue {
  sessions: ChatSession[];
  activeSession: ChatSession | null;
  activeSessionId: string | null;
  createSession: () => void;
  createTemporarySession: () => void;
  switchSession: (id: string) => void;
  deleteSession: (id: string) => void;
  /** Delete several chats at once (bulk bar). Picks a surviving chat as
   *  the new active one if the active chat is among them. */
  deleteSessions: (ids: string[]) => void;
  /** Pin/unpin a chat. Persisted server-side per client (see queries.ts). */
  setSessionPinned: (id: string, pinned: boolean) => void;
  renameSession: (id: string, title: string) => void;
  clearAllSessions: () => void;
  updateSessionMessages: (messages: StoredMessage[], accumulatedGraph?: AccumulatedGraph) => void;
  updateSessionMessagesById: (sessionId: string, messages: StoredMessage[], accumulatedGraph?: AccumulatedGraph) => void;
  /** Call once on stream-done. Invalidates the chat detail and list so
   *  the next read pulls server-truth (including the just-persisted
   *  turn's events). Safe to call for drafts — no-ops if the id isn't
   *  server-backed yet. */
  notifyTurnComplete: (chatId: string) => void;
  patchSession: (id: string, patch: Partial<ChatSession>) => void;
  // Folder management
  folders: ChatFolder[];
  createFolder: (name: string) => void;
  renameFolder: (id: string, name: string) => void;
  deleteFolder: (id: string) => void;
  moveSessionToFolder: (sessionId: string, folderId: string | undefined) => void;
  folderCollapseState: Record<string, boolean>;
  toggleFolderCollapse: (folderId: string) => void;
  // Export dialog (shared home so it's reachable from Sidebar's FolderMenu
  // and from page-level Export buttons alike)
  exportDialogOpen: boolean;
  openExportDialog: (scope?: BulkExportScope) => void;
  closeExportDialog: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

function newDraft(): ChatSession {
  const now = Date.now();
  return {
    id: crypto.randomUUID(),
    title: '',
    createdAt: now,
    updatedAt: now,
    messages: [],
  };
}

function readActiveIdFromStorage(): string | null {
  try {
    return sessionStorage.getItem(ACTIVE_ID_KEY);
  } catch {
    return null;
  }
}

function writeActiveIdToStorage(id: string | null): void {
  try {
    if (id) sessionStorage.setItem(ACTIVE_ID_KEY, id);
    else sessionStorage.removeItem(ACTIVE_ID_KEY);
  } catch {
    // Private browsing or quota — non-fatal; active id just won't survive reload.
  }
}

function graphsEqual(a?: AccumulatedGraph, b?: AccumulatedGraph): boolean {
  // Cheap structural check — node/edge counts + lastUpdated. Catches the
  // 99% case (nothing changed since last save) and avoids JSON.stringify
  // on every keystroke-driven graph update. False negatives just cost an
  // extra PATCH; correctness is unaffected (server is last-write-wins).
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.nodes.length === b.nodes.length &&
    a.edges.length === b.edges.length &&
    a.lastUpdated === b.lastUpdated &&
    a.queryCount === b.queryCount
  );
}

// ── Provider ───────────────────────────────────────────────────────────

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();

  // One-shot v2 migration. Fires on first mount; the flag short-circuits
  // subsequent loads. Refetches ['chats'] on success so the migrated
  // entries show up immediately without waiting for a re-mount.
  const migrationRunRef = useRef(false);
  useEffect(() => {
    if (migrationRunRef.current) return;
    migrationRunRef.current = true;
    void runV2Migration().then((res) => {
      if (res.ran && (res.imported > 0 || res.skipped > 0)) {
        qc.invalidateQueries({ queryKey: chatsKey() });
        qc.invalidateQueries({ queryKey: ['folders'] });
      }
    });
  }, [qc]);

  // Server-backed state
  const chatsQuery = useChats();
  const foldersQuery = useFolders();
  const collapseQuery = useFolderCollapse();

  // Local-only state: drafts (no turns yet), temps (never persisted),
  // active id. Drafts disappear from local state once a server entry
  // with the same id appears in chatsQuery.
  const [drafts, setDrafts] = useState<ChatSession[]>([]);
  const [temps, setTemps] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionIdState] = useState<string | null>(
    () => readActiveIdFromStorage(),
  );
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportDialogScope, setExportDialogScope] = useState<BulkExportScope | undefined>(undefined);

  const setActiveSessionId = useCallback((id: string | null) => {
    setActiveSessionIdState(id);
    writeActiveIdToStorage(id);
  }, []);

  // Server chats merged with local drafts/temps. Drafts whose id now
  // exists server-side are dropped from local state (they'll surface
  // through useChats instead).
  const serverChatsById = useMemo(() => {
    const m = new Map<string, ChatSummary>();
    for (const c of chatsQuery.data ?? []) m.set(c.chat_id, c);
    return m;
  }, [chatsQuery.data]);

  useEffect(() => {
    if (drafts.length === 0) return;
    const stillDrafts = drafts.filter((d) => !serverChatsById.has(d.id));
    if (stillDrafts.length !== drafts.length) setDrafts(stillDrafts);
  }, [serverChatsById, drafts]);

  // Skip the chat-detail query for drafts and temps — those have no
  // server row yet, so the GET would 404 on every render.
  const activeIsLocal =
    !!activeSessionId &&
    (drafts.some((d) => d.id === activeSessionId) ||
      temps.some((t) => t.id === activeSessionId));
  const activeChatQuery = useChat(activeIsLocal ? null : activeSessionId);

  // Sessions array exposed to consumers. Order: drafts/temps first
  // (newest user intent), then server chats by updated_at desc.
  // Pins ride in the folder-collapse map under `pin:<chat_id>` keys.
  const pinnedIds = useMemo(() => {
    const out = new Set<string>();
    for (const [k, v] of Object.entries(collapseQuery.data ?? {})) {
      if (v && k.startsWith(PIN_KEY_PREFIX)) out.add(k.slice(PIN_KEY_PREFIX.length));
    }
    return out;
  }, [collapseQuery.data]);

  const sessions: ChatSession[] = useMemo(() => {
    const serverSessions = (chatsQuery.data ?? []).map(summaryToSessionStub);
    const all = [...drafts, ...temps, ...serverSessions];
    if (pinnedIds.size === 0) return all;
    return all.map((s) => (pinnedIds.has(s.id) ? { ...s, pinned: true } : s));
  }, [drafts, temps, chatsQuery.data, pinnedIds]);

  // Active session resolution: draft/temp first, then server detail.
  // While useChat is loading we return null instead of the (empty-
  // messages) summary stub — ChatApp keys its hydration on the first
  // non-null activeSession for a given id; a stub would lock it in
  // permanently and the real messages would never paint.
  const activeSession: ChatSession | null = useMemo(() => {
    if (!activeSessionId) return null;
    const draft = drafts.find((d) => d.id === activeSessionId);
    if (draft) return draft;
    const temp = temps.find((t) => t.id === activeSessionId);
    if (temp) return temp;
    if (activeChatQuery.data && activeChatQuery.data.chat_id === activeSessionId) {
      const s = detailToSession(activeChatQuery.data);
      return pinnedIds.has(s.id) ? { ...s, pinned: true } : s;
    }
    return null;
  }, [activeSessionId, drafts, temps, activeChatQuery.data, pinnedIds]);

  // Refs so callbacks see the latest values without forcing re-creation.
  const activeIdRef = useRef(activeSessionId);
  useEffect(() => { activeIdRef.current = activeSessionId; }, [activeSessionId]);
  const draftsRef = useRef(drafts);
  useEffect(() => { draftsRef.current = drafts; }, [drafts]);
  const tempsRef = useRef(temps);
  useEffect(() => { tempsRef.current = temps; }, [temps]);

  // ── Accumulated graph sync (debounced, per-chat, flush on switch) ────
  //
  // Hazard: user edits graph in chat A, switches to B before debounce
  // fires. Without per-chat timers + flush, A's edit is dropped or sent
  // late (after we've moved on). Map keyed by chatId; each entry owns
  // its own timer.
  type Pending = { graph: AccumulatedGraph; timer: number; lastSeen: AccumulatedGraph | undefined };
  const pendingByChat = useRef<Map<string, Pending>>(new Map());

  const flushChat = useCallback(async (chatId: string) => {
    const entry = pendingByChat.current.get(chatId);
    if (!entry) return;
    pendingByChat.current.delete(chatId);
    clearTimeout(entry.timer);
    try {
      await updateAccumulatedGraph(chatId, entry.graph as unknown as Record<string, unknown>);
      qc.invalidateQueries({ queryKey: chatKey(chatId) });
    } catch (err) {
      console.warn('accumulated graph flush failed', err);
    }
  }, [qc]);

  const scheduleGraphPatch = useCallback((chatId: string, graph: AccumulatedGraph) => {
    const existing = pendingByChat.current.get(chatId);
    if (existing && graphsEqual(existing.lastSeen, graph)) return;
    if (existing) clearTimeout(existing.timer);
    const timer = window.setTimeout(() => {
      void flushChat(chatId);
    }, GRAPH_PATCH_DEBOUNCE_MS);
    pendingByChat.current.set(chatId, { graph, timer, lastSeen: graph });
  }, [flushChat]);

  // On active id change: flush every pending PATCH so any subsequent
  // useChat refetch (the new chat's, or the old one's on switch-back)
  // reads server-truth — no stale-then-fresh sequence that could
  // overwrite recent edits. Flushes run in parallel; each invalidates
  // its own chat's detail on completion.
  const prevActiveIdRef = useRef(activeSessionId);
  useEffect(() => {
    if (prevActiveIdRef.current !== activeSessionId) {
      const ids = Array.from(pendingByChat.current.keys());
      for (const id of ids) void flushChat(id);
    }
    prevActiveIdRef.current = activeSessionId;
  }, [activeSessionId, flushChat]);

  // On unmount and beforeunload, flush every pending chat.
  useEffect(() => {
    const onUnload = () => {
      // navigator.sendBeacon for best-effort durability across page close.
      // Body must be a Blob with JSON content-type for FastAPI to parse.
      for (const [chatId, entry] of pendingByChat.current.entries()) {
        try {
          clearTimeout(entry.timer);
          const body = new Blob(
            [JSON.stringify({ accumulated_graph: entry.graph })],
            { type: 'application/json' },
          );
          navigator.sendBeacon(
            `/api/fastapi/chats/${encodeURIComponent(chatId)}/accumulated-graph`,
            body,
          );
        } catch {
          // best-effort
        }
      }
      pendingByChat.current.clear();
    };
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      // Component unmount (route change, logout) — fire normal flushes.
      for (const chatId of Array.from(pendingByChat.current.keys())) {
        void flushChat(chatId);
      }
    };
  }, [flushChat]);

  // ── Mutations ────────────────────────────────────────────────────────

  const deleteChatMutation = useDeleteChat();
  const patchChatMutation = usePatchChat();
  const setChatFolderMutation = useSetChatFolder();
  const createFolderMutation = useCreateFolder();
  const patchFolderMutation = usePatchFolder();
  const deleteFolderMutation = useDeleteFolder();
  const setFolderCollapseMutation = useSetFolderCollapse();
  const setChatPinnedMutation = useSetChatPinned();
  const pinnedIdsRef = useRef(pinnedIds);
  useEffect(() => { pinnedIdsRef.current = pinnedIds; }, [pinnedIds]);

  // ── Session actions ──────────────────────────────────────────────────

  const createSession = useCallback(() => {
    const fresh = newDraft();
    // Drop a previous empty draft if the user is just clicking "new"
    // repeatedly so the sidebar doesn't accumulate ghosts.
    setDrafts((prev) => {
      const oldId = activeIdRef.current;
      const filtered = prev.filter((d) => !(d.id === oldId && d.messages.length === 0));
      return [fresh, ...filtered];
    });
    setActiveSessionId(fresh.id);
  }, [setActiveSessionId]);

  const createTemporarySession = useCallback(() => {
    const fresh = newDraft();
    fresh.temporary = true;
    setTemps((prev) => [fresh, ...prev]);
    setActiveSessionId(fresh.id);
  }, [setActiveSessionId]);

  const switchSession = useCallback((id: string) => {
    const oldId = activeIdRef.current;
    if (oldId && oldId !== id) {
      // Discard temps on leave; prune empty drafts.
      setTemps((prev) => prev.filter((t) => t.id !== oldId));
      setDrafts((prev) => {
        const old = prev.find((d) => d.id === oldId);
        if (old && old.messages.length === 0) return prev.filter((d) => d.id !== oldId);
        return prev;
      });
    }
    setActiveSessionId(id);
  }, [setActiveSessionId]);

  const deleteSession = useCallback((id: string) => {
    // Determine where the session lives. Drafts and temps are local-only.
    if (draftsRef.current.some((d) => d.id === id)) {
      setDrafts((prev) => prev.filter((d) => d.id !== id));
    } else if (tempsRef.current.some((t) => t.id === id)) {
      setTemps((prev) => prev.filter((t) => t.id !== id));
    } else {
      deleteChatMutation.mutate(id);
    }
    // Drop the pin row too so the per-client store doesn't collect orphans.
    if (pinnedIdsRef.current.has(id)) {
      setChatPinnedMutation.mutate({ chat_id: id, pinned: false });
    }
    if (activeIdRef.current === id) {
      // Switch to the next available session, or create a fresh draft.
      const next =
        (chatsQuery.data ?? []).find((c) => c.chat_id !== id)?.chat_id ?? null;
      if (next) setActiveSessionId(next);
      else {
        const fresh = newDraft();
        setDrafts((prev) => [fresh, ...prev]);
        setActiveSessionId(fresh.id);
      }
    }
  }, [chatsQuery.data, deleteChatMutation, setActiveSessionId, setChatPinnedMutation]);

  const deleteSessions = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    const doomed = new Set(ids);
    setDrafts((prev) => prev.filter((d) => !doomed.has(d.id)));
    setTemps((prev) => prev.filter((t) => !doomed.has(t.id)));
    for (const id of ids) {
      const local =
        draftsRef.current.some((d) => d.id === id) ||
        tempsRef.current.some((t) => t.id === id);
      if (!local) deleteChatMutation.mutate(id);
      if (pinnedIdsRef.current.has(id)) {
        setChatPinnedMutation.mutate({ chat_id: id, pinned: false });
      }
    }
    const activeId = activeIdRef.current;
    if (activeId && doomed.has(activeId)) {
      const next = (chatsQuery.data ?? []).find((c) => !doomed.has(c.chat_id))?.chat_id ?? null;
      if (next) setActiveSessionId(next);
      else {
        const fresh = newDraft();
        setDrafts((prev) => [fresh, ...prev]);
        setActiveSessionId(fresh.id);
      }
    }
  }, [chatsQuery.data, deleteChatMutation, setActiveSessionId, setChatPinnedMutation]);

  const setSessionPinned = useCallback((id: string, pinned: boolean) => {
    // Temporary chats are never persisted; don't leave a server row for them.
    if (tempsRef.current.some((t) => t.id === id)) return;
    setChatPinnedMutation.mutate({ chat_id: id, pinned });
  }, [setChatPinnedMutation]);

  const renameSession = useCallback((id: string, title: string) => {
    if (draftsRef.current.some((d) => d.id === id)) {
      setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, title } : d)));
      return;
    }
    if (tempsRef.current.some((t) => t.id === id)) {
      setTemps((prev) => prev.map((t) => (t.id === id ? { ...t, title } : t)));
      return;
    }
    patchChatMutation.mutate({ chat_id: id, patch: { title } });
  }, [patchChatMutation]);

  const clearAllSessions = useCallback(() => {
    const serverIds = (chatsQuery.data ?? []).map((c) => c.chat_id);
    setDrafts([]);
    setTemps([]);
    for (const id of serverIds) deleteChatMutation.mutate(id);
    for (const id of pinnedIdsRef.current) setChatPinnedMutation.mutate({ chat_id: id, pinned: false });
    const fresh = newDraft();
    setDrafts([fresh]);
    setActiveSessionId(fresh.id);
  }, [chatsQuery.data, deleteChatMutation, setActiveSessionId, setChatPinnedMutation]);

  // Update messages + (optionally) accumulated graph.
  //
  // Messages: NO-OP for durability. The server persists each turn at
  // stream completion via the existing _persist_turn path. ChatApp
  // already owns the in-flight display state; the query invalidation
  // fired by the stream-done callback refetches server truth on switch.
  //
  // Drafts: we still mirror messages into the local stub so the title
  // can render and switching mid-draft doesn't lose the in-progress
  // conversation before the first turn is persisted.
  //
  // Accumulated graph: schedules a debounced PATCH for server-backed
  // chats. For drafts/temps, just updates local state (no chat row yet).
  const updateSessionMessagesById = useCallback(
    (sessionId: string, messages: StoredMessage[], accumulatedGraph?: AccumulatedGraph) => {
      const isDraft = draftsRef.current.some((d) => d.id === sessionId);
      const isTemp = tempsRef.current.some((t) => t.id === sessionId);
      if (isDraft) {
        setDrafts((prev) =>
          prev.map((d) =>
            d.id === sessionId
              ? { ...d, messages, accumulatedGraph: accumulatedGraph ?? d.accumulatedGraph, updatedAt: Date.now() }
              : d,
          ),
        );
      } else if (isTemp) {
        setTemps((prev) =>
          prev.map((t) =>
            t.id === sessionId
              ? { ...t, messages, accumulatedGraph: accumulatedGraph ?? t.accumulatedGraph, updatedAt: Date.now() }
              : t,
          ),
        );
      } else if (accumulatedGraph !== undefined) {
        scheduleGraphPatch(sessionId, accumulatedGraph);
      }
    },
    [scheduleGraphPatch],
  );

  const updateSessionMessages = useCallback(
    (messages: StoredMessage[], accumulatedGraph?: AccumulatedGraph) => {
      const id = activeIdRef.current;
      if (!id) return;
      updateSessionMessagesById(id, messages, accumulatedGraph);
    },
    [updateSessionMessagesById],
  );

  const notifyTurnComplete = useCallback((chatId: string) => {
    // Promote draft to server-backed on first turn completion: the
    // server now has the chat row under the same id, useChats will pick
    // it up on next refetch.
    if (draftsRef.current.some((d) => d.id === chatId)) {
      setDrafts((prev) => prev.filter((d) => d.id !== chatId));
    }
    qc.invalidateQueries({ queryKey: chatKey(chatId) });
    qc.invalidateQueries({ queryKey: chatsKey() });
  }, [qc]);

  // Generic patch — only the fields that callers actually use today
  // (serverChatId, folderId, serverIsSensitive). serverChatId is a
  // historical compat affordance; in v2 session.id === serverChatId so
  // the patch is effectively a no-op for server-backed sessions.
  const patchSession = useCallback((id: string, patch: Partial<ChatSession>) => {
    // Drafts/temps: in-memory only.
    if (draftsRef.current.some((d) => d.id === id)) {
      setDrafts((prev) =>
        prev.map((d) => (d.id === id ? { ...d, ...patch, updatedAt: Date.now() } : d)),
      );
      return;
    }
    if (tempsRef.current.some((t) => t.id === id)) {
      setTemps((prev) =>
        prev.map((t) => (t.id === id ? { ...t, ...patch, updatedAt: Date.now() } : t)),
      );
      return;
    }
    // Server-backed: translate the parts that map to PATCH endpoints.
    if (patch.folderId !== undefined) {
      setChatFolderMutation.mutate({ chat_id: id, folder_id: patch.folderId ?? null });
    }
    if (patch.serverIsSensitive !== undefined) {
      patchChatMutation.mutate({ chat_id: id, patch: { is_sensitive: patch.serverIsSensitive } });
    }
    if (patch.title !== undefined) {
      patchChatMutation.mutate({ chat_id: id, patch: { title: patch.title } });
    }
    // serverChatId / messages / accumulatedGraph deliberately ignored.
  }, [patchChatMutation, setChatFolderMutation]);

  // ── Folder actions ───────────────────────────────────────────────────

  const folders: ChatFolder[] = useMemo(
    () =>
      (foldersQuery.data ?? []).map((f) => ({
        id: f.folder_id,
        name: f.name,
        createdAt: Date.parse(f.created_at) || Date.now(),
      })),
    [foldersQuery.data],
  );

  // Strip pin rows so consumers only see real folder ids.
  const folderCollapseState = useMemo(() => {
    const out: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(collapseQuery.data ?? {})) {
      if (!k.startsWith(PIN_KEY_PREFIX)) out[k] = v;
    }
    return out;
  }, [collapseQuery.data]);

  const createFolder = useCallback((name: string) => {
    createFolderMutation.mutate({ folder_id: crypto.randomUUID(), name });
  }, [createFolderMutation]);

  const renameFolder = useCallback((id: string, name: string) => {
    patchFolderMutation.mutate({ folder_id: id, name });
  }, [patchFolderMutation]);

  const deleteFolder = useCallback((id: string) => {
    deleteFolderMutation.mutate(id);
  }, [deleteFolderMutation]);

  const moveSessionToFolder = useCallback((sessionId: string, folderId: string | undefined) => {
    // Drafts/temps: just update local state.
    if (draftsRef.current.some((d) => d.id === sessionId)) {
      setDrafts((prev) =>
        prev.map((d) => (d.id === sessionId ? { ...d, folderId } : d)),
      );
      return;
    }
    setChatFolderMutation.mutate({ chat_id: sessionId, folder_id: folderId ?? null });
  }, [setChatFolderMutation]);

  const toggleFolderCollapse = useCallback((folderId: string) => {
    const current = (collapseQuery.data ?? {})[folderId] ?? false;
    setFolderCollapseMutation.mutate({ folder_id: folderId, collapsed: !current });
  }, [collapseQuery.data, setFolderCollapseMutation]);

  // ── Export dialog ────────────────────────────────────────────────────

  const openExportDialog = useCallback((scope?: BulkExportScope) => {
    setExportDialogScope(scope);
    setExportDialogOpen(true);
  }, []);

  const closeExportDialog = useCallback(() => {
    setExportDialogOpen(false);
  }, []);

  // Bootstrap: ensure activeSessionId resolves to a real session once
  // useChats returns. Also detects stale ids — sessionStorage may
  // point at a chat that was deleted on another device, in which case
  // useChat would 404 forever; we recover by picking the most recent
  // server chat or minting a fresh draft.
  useEffect(() => {
    if (chatsQuery.isLoading) return;
    const idIsValid =
      !!activeSessionId &&
      (drafts.some((d) => d.id === activeSessionId) ||
        temps.some((t) => t.id === activeSessionId) ||
        serverChatsById.has(activeSessionId));
    if (idIsValid) return;
    if (drafts.length > 0) {
      setActiveSessionId(drafts[0].id);
      return;
    }
    const first = chatsQuery.data?.[0];
    if (first) {
      setActiveSessionId(first.chat_id);
      return;
    }
    // No sessions anywhere → mint a draft so the user can start typing.
    const fresh = newDraft();
    setDrafts([fresh]);
    setActiveSessionId(fresh.id);
  }, [activeSessionId, chatsQuery.isLoading, chatsQuery.data, drafts, temps, serverChatsById, setActiveSessionId]);

  const value: SessionContextValue = {
    sessions,
    activeSession,
    activeSessionId,
    createSession,
    createTemporarySession,
    switchSession,
    deleteSession,
    deleteSessions,
    setSessionPinned,
    renameSession,
    clearAllSessions,
    updateSessionMessages,
    updateSessionMessagesById,
    notifyTurnComplete,
    patchSession,
    folders,
    createFolder,
    renameFolder,
    deleteFolder,
    moveSessionToFolder,
    folderCollapseState,
    toggleFolderCollapse,
    exportDialogOpen,
    openExportDialog,
    closeExportDialog,
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
      {exportDialogOpen && (
        <ExportDialog sessions={sessions} folders={folders} initialScope={exportDialogScope} onClose={closeExportDialog} />
      )}
    </SessionContext.Provider>
  );
}

export function useSessionContext(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSessionContext must be used within a SessionProvider');
  }
  return ctx;
}
